import { injectable } from 'inversify';

import BaseService from '@core/services/base';
import Axios, { AxiosInstance } from 'axios';
import { AuthProvider, Profile, SocialAuthProvider } from '@shared/models/profile';
import container from '@core/di';
import HttpClient from '@core/HttpClient';
import { INSTANCES } from '@core/HttpClient/httpClient';
import Config from '@core/config/config';

@injectable()
export default class ProfileService extends BaseService {
  static diToken = Symbol('profile-service');
  private _ssoHttpClient?: AxiosInstance;

  constructor() {
    super({ domainArea: 'user-accounts' });
  }

  async getProfile() {
    const { data } = await Axios.get<Profile>(this.getURL('current'));

    return data;
  }

  update(data) {
    return Axios.put<Profile>(this.getURL('current'), data);
  }

  removeAvatar() {
    return Axios.delete<Profile>(this.getURL('current/user-image-profile'));
  }

  uploadAvatar(formData) {
    return Axios.put(this.getURL('current/user-image-profile'), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  removeUser(ssoId: string) {
    return Axios.delete(this.getURL(`${ssoId}`));
  }

  async getLinkedProviders(): Promise<SocialAuthProvider[]> {
    const resp = await this.ssoHttpClient.get<AuthProvider[]>('/external-link/list');

    return resp.data.filter(d => d !== 'Password').map(p => p as SocialAuthProvider);
  }

  async unlinkProvider(provider: SocialAuthProvider) {
    return await this.ssoHttpClient.delete('/external-link/remove', {
      data: JSON.stringify(provider),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async changePassword(oldPassword: string, newPassword: string) {
    return await this.ssoHttpClient.post('/users/current/password-change-confirm', {
      oldPassword,
      newPassword,
    });
  }

  async sendChangeEmailCode(newEmail: string) {
    return Axios.post(this.getURL(`send-change-email-code/${newEmail}`));
  }

  async changeEmailWithCode(newEmail: string, activationCode: string) {
    return Axios.post(this.getURL('change-email-with-code'), { newEmail, activationCode });
  }

  getAuthUrls(ssoId: string): Record<SocialAuthProvider, string> {
    const config = container.get<Config>(Config.diToken);
    const { googleClientId, facebookClientId, appleClientId, apiSSO } = config.get();

    const webAppUrl = window.location.origin;

    // Build OAuth URL with state containing ssoId and redirect URL
    const stateData = JSON.stringify({
      ssoId,
      redirectUri: `${webAppUrl}/profile`,
    });

    const urls: Record<SocialAuthProvider, string> = {
      Google: this.getAuthUrl(
        'https://accounts.google.com/o/oauth2/v2/auth',
        googleClientId + '',
        `${apiSSO.origin}/web-google/callback`,
        'openid profile email',
        stateData,
        {
          access_type: 'offline',
          prompt: 'consent',
        },
      ),
      Facebook: this.getAuthUrl(
        'https://www.facebook.com/v18.0/dialog/oauth',
        facebookClientId + '',
        `${apiSSO.origin}/web-facebook/callback`,
        'public_profile email',
        stateData,
        null,
      ),
      Apple: this.getAuthUrl(
        'https://appleid.apple.com/auth/authorize',
        appleClientId + '',
        `${apiSSO.origin}/web-apple/callback`,
        'name email',
        stateData,
        {
          response_mode: 'form_post',
        },
      ),
    };

    return urls;
  }

  private get ssoHttpClient(): AxiosInstance {
    if (!this._ssoHttpClient) {
      this._ssoHttpClient = container
        .get<HttpClient>(HttpClient.diToken)
        .getInstance(INSTANCES.SSO_INSTANCE);
    }
    return this._ssoHttpClient!;
  }

  private getAuthUrl(
    authEndpoint: string,
    clientId: string,
    redirectUri: string,
    scope: string,
    stateData: string,
    extraAuthParameters: any,
  ): string {
    const params: Record<string, string> = {
      client_id: clientId,
      redirect_uri: redirectUri || '',
      response_type: 'code',
      scope: scope || '',
      state: stateData,
    };

    if (extraAuthParameters) {
      Object.assign(params, extraAuthParameters);
    }

    const urlParams = new URLSearchParams(params);

    return `${authEndpoint}?${urlParams.toString()}`;
  }
}
